import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import { api } from '../../lib/api';

const MODULE_LABELS = {
  case: 'Case study',
  drill: 'Calculation',
  'spot-error': 'Spot-the-error',
  outpatient: 'Outpatient',
  cpd: 'CPD',
};

const MODULE_BADGE = {
  case: 'primary',
  drill: 'info',
  'spot-error': 'warning',
  outpatient: 'dark',
  cpd: 'light',
};

const TYPE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'case', label: 'Case studies' },
  { value: 'outpatient', label: 'Outpatient' },
  { value: 'drill', label: 'Calc exercises' },
  { value: 'spot-error', label: 'Spot-the-error' },
  { value: 'cpd', label: 'CPD entries' },
];

const REFLECTION_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'reflected', label: 'Has reflection' },
  { value: 'unreflected', label: 'Needs reflection' },
];

const CPD_TYPE_LABELS = {
  article: 'Article / reading',
  course: 'Course / training',
  audit: 'Audit',
  intervention: 'Clinical intervention',
  other: 'Other',
};

const CPD_TYPE_OPTIONS = Object.entries(CPD_TYPE_LABELS).map(([value, label]) => ({ value, label }));

const todayIso = () => new Date().toISOString().slice(0, 10);

const formatDate = (value) => {
  if (!value) return '—';
  try {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return new Date(`${value}T00:00:00`).toLocaleDateString('en-GB');
    }
    return new Date(value).toLocaleString('en-GB');
  } catch {
    return String(value);
  }
};

const formatDateOnly = (value) => {
  if (!value) return '—';
  try {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return new Date(`${value}T00:00:00`).toLocaleDateString('en-GB');
    }
    return new Date(value).toLocaleDateString('en-GB');
  } catch {
    return String(value);
  }
};

const entryDateForCompare = (entry) => {
  const v = entry.occurredAt;
  if (!v) return 0;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    return new Date(`${v}T00:00:00`).getTime();
  }
  return new Date(v).getTime();
};

const entryDateIso = (entry) => {
  const v = entry.occurredAt;
  if (!v) return '';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  try { return new Date(v).toISOString().slice(0, 10); } catch { return ''; }
};

const emptyCpdForm = () => ({
  id: null,
  title: '',
  entryType: 'article',
  hours: '1',
  reflection: '',
  occurredOn: todayIso(),
});

const StudentPortfolioView = ({ token, onBack }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [reflectionFilter, setReflectionFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('all');

  const [reflectionDrafts, setReflectionDrafts] = useState({});
  const [savingReflectionKey, setSavingReflectionKey] = useState(null);
  const [reflectionFeedback, setReflectionFeedback] = useState({});

  const [showCpdModal, setShowCpdModal] = useState(false);
  const [cpdForm, setCpdForm] = useState(emptyCpdForm());
  const [cpdSaving, setCpdSaving] = useState(false);
  const [cpdError, setCpdError] = useState('');

  const [confirmDeleteCpdId, setConfirmDeleteCpdId] = useState(null);
  const [deletingCpdId, setDeletingCpdId] = useState(null);

  const [frameworks, setFrameworks] = useState([]);
  const [outcomesByFramework, setOutcomesByFramework] = useState({});
  const [frameworkBusy, setFrameworkBusy] = useState({});
  const [showFrameworkModal, setShowFrameworkModal] = useState(false);

  const [tagModalEntry, setTagModalEntry] = useState(null);
  const [tagSelection, setTagSelection] = useState(new Set());
  const [tagSaving, setTagSaving] = useState(false);
  const [tagError, setTagError] = useState('');

  // Named portfolios (PR follow-up). "All activity" is selectedPortfolioId === ''
  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '' });
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState('');

  const [showManageModal, setShowManageModal] = useState(false);
  const [editingPortfolioId, setEditingPortfolioId] = useState(null);
  const [editingForm, setEditingForm] = useState({ name: '', description: '' });
  const [manageBusy, setManageBusy] = useState(false);
  const [confirmDeletePortfolioId, setConfirmDeletePortfolioId] = useState(null);

  const [membershipModalEntry, setMembershipModalEntry] = useState(null);
  const [membershipSelection, setMembershipSelection] = useState(new Set());
  const [membershipBaseline, setMembershipBaseline] = useState(new Set());
  const [membershipSaving, setMembershipSaving] = useState(false);
  const [membershipError, setMembershipError] = useState('');

  // External share links for the currently-selected portfolio.
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLinks, setShareLinks] = useState([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareForm, setShareForm] = useState({ label: '', expiresInDays: '' });
  const [shareCreating, setShareCreating] = useState(false);
  const [shareLinkBusy, setShareLinkBusy] = useState({});
  const [copiedLinkId, setCopiedLinkId] = useState('');

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const result = await api.getPortfolio(token);
      setEntries(Array.isArray(result.entries) ? result.entries : []);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load portfolio.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const refreshFrameworks = useCallback(async () => {
    if (!token) return;
    try {
      const result = await api.getPortfolioFrameworks(token);
      setFrameworks(Array.isArray(result.frameworks) ? result.frameworks : []);
    } catch (err) {
      // Don't block the page on framework load - tagging is optional
      console.error('frameworks load failed', err);
    }
  }, [token]);

  const refreshPortfolios = useCallback(async () => {
    if (!token) return;
    try {
      const result = await api.listPortfolios(token);
      setPortfolios(Array.isArray(result.portfolios) ? result.portfolios : []);
    } catch (err) {
      console.error('portfolios load failed', err);
    }
  }, [token]);

  const [activeTab, setActiveTab] = useState('entries');
  const [goals, setGoals] = useState([]);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [goalsError, setGoalsError] = useState('');
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [goalForm, setGoalForm] = useState({
    id: null, title: '', description: '', status: 'in_progress', targetDate: '',
  });
  const [goalSaving, setGoalSaving] = useState(false);
  const [goalError, setGoalError] = useState('');
  const [confirmDeleteGoalId, setConfirmDeleteGoalId] = useState(null);
  const [goalLinkPicker, setGoalLinkPicker] = useState(null);
  const [goalLinkSaving, setGoalLinkSaving] = useState(false);
  const [goalLinkError, setGoalLinkError] = useState('');

  const refreshGoals = useCallback(async () => {
    if (!token) return;
    try {
      setGoalsLoading(true);
      const result = await api.listPortfolioGoals(token);
      setGoals(Array.isArray(result.goals) ? result.goals : []);
      setGoalsError('');
    } catch (err) {
      setGoalsError(err?.message || 'Failed to load goals.');
    } finally {
      setGoalsLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { refreshFrameworks(); }, [refreshFrameworks]);
  useEffect(() => { refreshPortfolios(); }, [refreshPortfolios]);
  useEffect(() => { refreshGoals(); }, [refreshGoals]);

  // Load outcomes for each currently-enabled framework (lazy: skip if cached)
  useEffect(() => {
    if (!token) return;
    const enabled = frameworks.filter((f) => f.enabled);
    let cancelled = false;
    (async () => {
      for (const fw of enabled) {
        if (outcomesByFramework[fw.id]) continue;
        try {
          const result = await api.getPortfolioOutcomes(fw.id, token);
          if (cancelled) return;
          setOutcomesByFramework((prev) => ({
            ...prev,
            [fw.id]: result.outcomes || [],
          }));
        } catch (err) {
          console.error('outcomes load failed', fw.id, err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [frameworks, outcomesByFramework, token]);

  const enabledFrameworks = useMemo(
    () => frameworks.filter((f) => f.enabled),
    [frameworks]
  );

  const currentPortfolio = useMemo(() => {
    if (!selectedPortfolioId) return null;
    return portfolios.find((p) => p.id === selectedPortfolioId) || null;
  }, [portfolios, selectedPortfolioId]);

  // Flat list of leaf outcomes (those with a parent) from all enabled frameworks,
  // labelled with their domain for grouped display in pickers + filters.
  const tagOptions = useMemo(() => {
    const options = [];
    for (const fw of enabledFrameworks) {
      const items = outcomesByFramework[fw.id] || [];
      const domainsById = new Map(
        items.filter((o) => o.parentId === null).map((o) => [o.id, o])
      );
      for (const o of items) {
        if (o.parentId === null) continue;
        const domain = domainsById.get(o.parentId);
        options.push({
          frameworkId: fw.id,
          frameworkSlug: fw.slug,
          frameworkName: fw.name,
          domainCode: domain?.code || '',
          domainLabel: domain?.label || '',
          id: o.id,
          code: o.code,
          label: o.label,
        });
      }
    }
    return options;
  }, [enabledFrameworks, outcomesByFramework]);

  const visible = useMemo(() => {
    return entries.filter((e) => {
      if (selectedPortfolioId) {
        const ids = Array.isArray(e.portfolioIds) ? e.portfolioIds : [];
        if (!ids.includes(selectedPortfolioId)) return false;
      }
      if (typeFilter !== 'all' && (e.entryKind || e.moduleType) !== typeFilter) return false;
      if (reflectionFilter === 'reflected' && !(e.reflection?.body && e.reflection.body.trim())) return false;
      if (reflectionFilter === 'unreflected' && e.reflection?.body && e.reflection.body.trim()) return false;
      if (outcomeFilter !== 'all') {
        const tags = Array.isArray(e.tags) ? e.tags : [];
        if (outcomeFilter === 'untagged') {
          if (tags.length > 0) return false;
        } else if (outcomeFilter === 'tagged') {
          if (tags.length === 0) return false;
        } else if (!tags.some((t) => t.outcomeItemId === outcomeFilter)) {
          return false;
        }
      }
      if (fromDate || toDate) {
        const ts = entryDateForCompare(e);
        if (!ts) return false;
        if (fromDate) {
          const f = new Date(`${fromDate}T00:00:00`).getTime();
          if (ts < f) return false;
        }
        if (toDate) {
          const t = new Date(`${toDate}T23:59:59`).getTime();
          if (ts > t) return false;
        }
      }
      return true;
    });
  }, [entries, selectedPortfolioId, typeFilter, reflectionFilter, outcomeFilter, fromDate, toDate]);

  // Summary reflects the filtered list (selected portfolio + any active
  // filters), so the numbers match what the user actually sees. Switching
  // to "All activity" widens the scope back to everything.
  const summary = useMemo(() => {
    const byType = {};
    let totalCpdHours = 0;
    let reflectedCount = 0;
    let taggedCount = 0;
    for (const e of visible) {
      const t = e.entryKind || e.moduleType;
      byType[t] = (byType[t] || 0) + 1;
      if (e.kind === 'cpd') totalCpdHours += Number(e.hours) || 0;
      if (e.reflection?.body && e.reflection.body.trim()) reflectedCount += 1;
      if (Array.isArray(e.tags) && e.tags.length) taggedCount += 1;
    }
    return {
      total: visible.length,
      byType,
      totalCpdHours,
      reflectedCount,
      taggedCount,
    };
  }, [visible]);

  const entryKey = (e) => `${e.entryKind}:${e.entryId}`;

  const reflectionValueFor = (e) => {
    const key = entryKey(e);
    if (Object.prototype.hasOwnProperty.call(reflectionDrafts, key)) return reflectionDrafts[key];
    return e.reflection?.body || '';
  };

  const handleReflectionChange = (e, value) => {
    setReflectionDrafts((prev) => ({ ...prev, [entryKey(e)]: value }));
    setReflectionFeedback((prev) => ({ ...prev, [entryKey(e)]: '' }));
  };

  const handleSaveReflection = async (e) => {
    const key = entryKey(e);
    const body = (reflectionDrafts[key] ?? e.reflection?.body ?? '').trim();
    setSavingReflectionKey(key);
    try {
      if (body) {
        const result = await api.upsertPortfolioReflection(
          { entryKind: e.entryKind, entryId: e.entryId, body },
          token
        );
        setEntries((prev) => prev.map((row) => (
          entryKey(row) === key ? { ...row, reflection: result.reflection } : row
        )));
        setReflectionFeedback((prev) => ({ ...prev, [key]: 'Saved' }));
      } else {
        await api.deletePortfolioReflection(
          { entryKind: e.entryKind, entryId: e.entryId },
          token
        );
        setEntries((prev) => prev.map((row) => (
          entryKey(row) === key ? { ...row, reflection: null } : row
        )));
        setReflectionFeedback((prev) => ({ ...prev, [key]: 'Cleared' }));
      }
      setReflectionDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (err) {
      setReflectionFeedback((prev) => ({ ...prev, [key]: err?.message || 'Save failed' }));
    } finally {
      setSavingReflectionKey(null);
    }
  };

  const openCpdCreate = () => {
    setCpdForm(emptyCpdForm());
    setCpdError('');
    setShowCpdModal(true);
  };

  const openCpdEdit = (entry) => {
    setCpdForm({
      id: entry.entryId,
      title: entry.sourceTitle || '',
      entryType: entry.entryType || 'article',
      hours: entry.hours == null ? '0' : String(entry.hours),
      reflection: entry.cpdReflection || '',
      occurredOn: entryDateIso(entry) || todayIso(),
    });
    setCpdError('');
    setShowCpdModal(true);
  };

  const handleCpdSave = async () => {
    const payload = {
      title: (cpdForm.title || '').trim(),
      entryType: cpdForm.entryType,
      hours: Number(cpdForm.hours),
      reflection: cpdForm.reflection || '',
      occurredOn: cpdForm.occurredOn,
    };
    if (!payload.title) {
      setCpdError('Title is required.');
      return;
    }
    if (!Number.isFinite(payload.hours) || payload.hours < 0 || payload.hours > 24) {
      setCpdError('Hours must be between 0 and 24.');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(payload.occurredOn)) {
      setCpdError('Date is required (YYYY-MM-DD).');
      return;
    }
    setCpdSaving(true);
    try {
      if (cpdForm.id) {
        await api.updateCpdEntry(cpdForm.id, payload, token);
      } else {
        const result = await api.createCpdEntry(payload, token);
        // If the student is currently viewing a specific portfolio, add the
        // new CPD entry to it automatically so it appears in the list
        // immediately. Without this the entry would only show up in
        // "All activity" and the student might think it was lost.
        if (selectedPortfolioId && result?.entry?.id) {
          try {
            await api.addPortfolioEntry(
              selectedPortfolioId,
              { entryKind: 'cpd', entryId: result.entry.id },
              token
            );
            await refreshPortfolios();
          } catch (err) {
            console.error('Failed to add new CPD entry to current portfolio', err);
          }
        }
      }
      setShowCpdModal(false);
      await refresh();
    } catch (err) {
      setCpdError(err?.payload?.error || err?.message || 'Save failed.');
    } finally {
      setCpdSaving(false);
    }
  };

  const handleDeleteCpd = async () => {
    if (!confirmDeleteCpdId) return;
    setDeletingCpdId(confirmDeleteCpdId);
    try {
      await api.deleteCpdEntry(confirmDeleteCpdId, token);
      setConfirmDeleteCpdId(null);
      await refresh();
    } catch (err) {
      setError(err?.message || 'Failed to delete CPD entry.');
    } finally {
      setDeletingCpdId(null);
    }
  };

  const handleToggleFramework = async (framework) => {
    setFrameworkBusy((prev) => ({ ...prev, [framework.id]: true }));
    try {
      if (framework.enabled) {
        await api.disablePortfolioFramework(framework.id, token);
      } else {
        await api.enablePortfolioFramework(framework.id, token);
      }
      await refreshFrameworks();
    } catch (err) {
      setError(err?.message || 'Failed to update framework.');
    } finally {
      setFrameworkBusy((prev) => ({ ...prev, [framework.id]: false }));
    }
  };

  const openTagModal = (entry) => {
    setTagModalEntry(entry);
    const existing = new Set((entry.tags || []).map((t) => t.outcomeItemId));
    setTagSelection(existing);
    setTagError('');
  };

  const toggleTagSelection = (id) => {
    setTagSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSaveTags = async () => {
    if (!tagModalEntry) return;
    setTagSaving(true);
    setTagError('');
    try {
      const result = await api.setPortfolioTags(
        {
          entryKind: tagModalEntry.entryKind,
          entryId: tagModalEntry.entryId,
          outcomeItemIds: [...tagSelection],
        },
        token
      );
      const key = entryKey(tagModalEntry);
      setEntries((prev) => prev.map((row) => (
        entryKey(row) === key ? { ...row, tags: result.tags || [] } : row
      )));
      setTagModalEntry(null);
    } catch (err) {
      setTagError(err?.payload?.error || err?.message || 'Failed to save tags.');
    } finally {
      setTagSaving(false);
    }
  };

  const resetFilters = () => {
    setTypeFilter('all');
    setReflectionFilter('all');
    setOutcomeFilter('all');
    setFromDate('');
    setToDate('');
  };

  const handleCreatePortfolio = async () => {
    const name = (createForm.name || '').trim();
    if (!name) {
      setCreateError('Name is required.');
      return;
    }
    setCreateSaving(true);
    setCreateError('');
    try {
      const result = await api.createPortfolio(
        { name, description: createForm.description || '' },
        token
      );
      await refreshPortfolios();
      if (result?.portfolio?.id) setSelectedPortfolioId(result.portfolio.id);
      setShowCreateModal(false);
      setCreateForm({ name: '', description: '' });
    } catch (err) {
      setCreateError(err?.payload?.error || err?.message || 'Failed to create portfolio.');
    } finally {
      setCreateSaving(false);
    }
  };

  const openEditPortfolio = (portfolio) => {
    setEditingPortfolioId(portfolio.id);
    setEditingForm({ name: portfolio.name, description: portfolio.description || '' });
  };

  const handleSavePortfolioEdit = async () => {
    if (!editingPortfolioId) return;
    setManageBusy(true);
    try {
      await api.updatePortfolio(
        editingPortfolioId,
        { name: editingForm.name.trim(), description: editingForm.description },
        token
      );
      setEditingPortfolioId(null);
      setEditingForm({ name: '', description: '' });
      await refreshPortfolios();
    } catch (err) {
      setError(err?.payload?.error || err?.message || 'Failed to update portfolio.');
    } finally {
      setManageBusy(false);
    }
  };

  const handleConfirmDeletePortfolio = async () => {
    if (!confirmDeletePortfolioId) return;
    setManageBusy(true);
    try {
      await api.deletePortfolio(confirmDeletePortfolioId, token);
      if (selectedPortfolioId === confirmDeletePortfolioId) {
        setSelectedPortfolioId('');
      }
      setConfirmDeletePortfolioId(null);
      await refreshPortfolios();
      await refresh();
    } catch (err) {
      setError(err?.payload?.error || err?.message || 'Failed to delete portfolio.');
    } finally {
      setManageBusy(false);
    }
  };

  const openMembershipModal = (entry) => {
    const ids = Array.isArray(entry.portfolioIds) ? entry.portfolioIds : [];
    setMembershipModalEntry(entry);
    setMembershipBaseline(new Set(ids));
    setMembershipSelection(new Set(ids));
    setMembershipError('');
  };

  const toggleMembership = (portfolioId) => {
    setMembershipSelection((prev) => {
      const next = new Set(prev);
      if (next.has(portfolioId)) next.delete(portfolioId); else next.add(portfolioId);
      return next;
    });
  };

  const handleSaveMembership = async () => {
    if (!membershipModalEntry) return;
    const baseline = membershipBaseline;
    const selection = membershipSelection;
    const toAdd = [...selection].filter((id) => !baseline.has(id));
    const toRemove = [...baseline].filter((id) => !selection.has(id));
    if (toAdd.length === 0 && toRemove.length === 0) {
      setMembershipModalEntry(null);
      return;
    }
    setMembershipSaving(true);
    setMembershipError('');
    try {
      for (const id of toAdd) {
        await api.addPortfolioEntry(
          id,
          {
            entryKind: membershipModalEntry.entryKind,
            entryId: membershipModalEntry.entryId,
          },
          token
        );
      }
      for (const id of toRemove) {
        await api.removePortfolioEntry(
          id,
          {
            entryKind: membershipModalEntry.entryKind,
            entryId: membershipModalEntry.entryId,
          },
          token
        );
      }
      const key = `${membershipModalEntry.entryKind}:${membershipModalEntry.entryId}`;
      setEntries((prev) => prev.map((row) => (
        `${row.entryKind}:${row.entryId}` === key
          ? { ...row, portfolioIds: [...selection] }
          : row
      )));
      await refreshPortfolios();
      setMembershipModalEntry(null);
    } catch (err) {
      setMembershipError(err?.payload?.error || err?.message || 'Failed to update membership.');
    } finally {
      setMembershipSaving(false);
    }
  };

  const removeFromCurrentPortfolio = async (entry) => {
    if (!selectedPortfolioId) return;
    try {
      await api.removePortfolioEntry(
        selectedPortfolioId,
        { entryKind: entry.entryKind, entryId: entry.entryId },
        token
      );
      const key = `${entry.entryKind}:${entry.entryId}`;
      setEntries((prev) => prev.map((row) => {
        if (`${row.entryKind}:${row.entryId}` !== key) return row;
        const next = (row.portfolioIds || []).filter((id) => id !== selectedPortfolioId);
        return { ...row, portfolioIds: next };
      }));
      await refreshPortfolios();
    } catch (err) {
      setError(err?.payload?.error || err?.message || 'Failed to remove from portfolio.');
    }
  };

  const refreshShareLinks = useCallback(async (portfolioId) => {
    if (!portfolioId || !token) return;
    try {
      setShareLoading(true);
      setShareError('');
      const result = await api.listPortfolioShareLinks(portfolioId, token);
      setShareLinks(Array.isArray(result.links) ? result.links : []);
    } catch (err) {
      setShareError(err?.payload?.error || err?.message || 'Failed to load share links.');
    } finally {
      setShareLoading(false);
    }
  }, [token]);

  const handleOpenShareModal = async () => {
    if (!selectedPortfolioId) return;
    setShowShareModal(true);
    setShareForm({ label: '', expiresInDays: '' });
    setCopiedLinkId('');
    await refreshShareLinks(selectedPortfolioId);
  };

  const handleCreateShareLink = async () => {
    if (!selectedPortfolioId) return;
    const payload = {};
    const trimmedLabel = (shareForm.label || '').trim();
    if (trimmedLabel) payload.label = trimmedLabel;
    if (shareForm.expiresInDays) {
      const days = Number(shareForm.expiresInDays);
      if (!Number.isFinite(days) || days <= 0) {
        setShareError('Expiry must be a positive number of days.');
        return;
      }
      payload.expiresAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
    }
    setShareCreating(true);
    setShareError('');
    try {
      await api.createPortfolioShareLink(selectedPortfolioId, payload, token);
      setShareForm({ label: '', expiresInDays: '' });
      await refreshShareLinks(selectedPortfolioId);
    } catch (err) {
      setShareError(err?.payload?.error || err?.message || 'Failed to create share link.');
    } finally {
      setShareCreating(false);
    }
  };

  const handleToggleShareRevoke = async (link) => {
    if (!selectedPortfolioId) return;
    setShareLinkBusy((prev) => ({ ...prev, [link.id]: true }));
    try {
      await api.updatePortfolioShareLink(
        selectedPortfolioId,
        link.id,
        { revoked: !link.revokedAt },
        token
      );
      await refreshShareLinks(selectedPortfolioId);
    } catch (err) {
      setShareError(err?.payload?.error || err?.message || 'Failed to update link.');
    } finally {
      setShareLinkBusy((prev) => ({ ...prev, [link.id]: false }));
    }
  };

  const handleDeleteShareLink = async (link) => {
    if (!selectedPortfolioId) return;
    setShareLinkBusy((prev) => ({ ...prev, [link.id]: true }));
    try {
      await api.deletePortfolioShareLink(selectedPortfolioId, link.id, token);
      await refreshShareLinks(selectedPortfolioId);
    } catch (err) {
      setShareError(err?.payload?.error || err?.message || 'Failed to delete link.');
    } finally {
      setShareLinkBusy((prev) => ({ ...prev, [link.id]: false }));
    }
  };

  const buildShareUrl = (token) => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/portfolio/share/${token}`;
  };

  const handleCopyShareLink = async (link) => {
    const url = buildShareUrl(link.token);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLinkId(link.id);
      setTimeout(() => setCopiedLinkId((prev) => (prev === link.id ? '' : prev)), 1500);
    } catch {
      // Clipboard may be blocked - fall back to a prompt so the user can copy manually.
      // eslint-disable-next-line no-alert
      window.prompt('Copy this share URL:', url);
    }
  };

  const openGoalCreate = () => {
    setGoalForm({ id: null, title: '', description: '', status: 'in_progress', targetDate: '' });
    setGoalError('');
    setShowGoalModal(true);
  };

  const openGoalEdit = (goal) => {
    setGoalForm({
      id: goal.id,
      title: goal.title,
      description: goal.description || '',
      status: goal.status,
      targetDate: goal.targetDate || '',
    });
    setGoalError('');
    setShowGoalModal(true);
  };

  const handleGoalSave = async () => {
    const title = (goalForm.title || '').trim();
    if (!title) {
      setGoalError('Title is required.');
      return;
    }
    setGoalSaving(true);
    try {
      const payload = {
        title,
        description: goalForm.description || '',
        status: goalForm.status || 'in_progress',
        targetDate: goalForm.targetDate || null,
      };
      if (goalForm.id) {
        await api.updatePortfolioGoal(goalForm.id, payload, token);
      } else {
        await api.createPortfolioGoal(payload, token);
      }
      setShowGoalModal(false);
      await refreshGoals();
    } catch (err) {
      setGoalError(err?.payload?.error || err?.message || 'Save failed');
    } finally {
      setGoalSaving(false);
    }
  };

  const handleGoalStatusToggle = async (goal) => {
    const nextStatus = goal.status === 'achieved' ? 'in_progress' : 'achieved';
    try {
      await api.updatePortfolioGoal(goal.id, { status: nextStatus }, token);
      await refreshGoals();
    } catch (err) {
      console.error('goal status toggle failed', err);
    }
  };

  const handleGoalDelete = async () => {
    if (!confirmDeleteGoalId) return;
    try {
      await api.deletePortfolioGoal(confirmDeleteGoalId, token);
      setConfirmDeleteGoalId(null);
      await refreshGoals();
    } catch (err) {
      console.error('goal delete failed', err);
    }
  };

  const openGoalLinkPicker = (goal) => {
    const baseline = new Set((goal.links || []).map((l) => `${l.entryKind}:${l.entryId}`));
    setGoalLinkPicker({
      goalId: goal.id,
      goalTitle: goal.title,
      baseline,
      selection: new Set(baseline),
    });
    setGoalLinkError('');
  };

  const handleGoalLinkSave = async () => {
    if (!goalLinkPicker) return;
    const { goalId, baseline, selection } = goalLinkPicker;
    const toAdd = [...selection].filter((k) => !baseline.has(k));
    const toRemove = [...baseline].filter((k) => !selection.has(k));
    setGoalLinkSaving(true);
    try {
      for (const key of toAdd) {
        const [entryKind, entryId] = key.split(':');
        await api.linkPortfolioGoalEntry(goalId, { entryKind, entryId }, token);
      }
      for (const key of toRemove) {
        const [entryKind, entryId] = key.split(':');
        await api.unlinkPortfolioGoalEntry(goalId, { entryKind, entryId }, token);
      }
      setGoalLinkPicker(null);
      await refreshGoals();
    } catch (err) {
      setGoalLinkError(err?.payload?.error || err?.message || 'Save failed');
    } finally {
      setGoalLinkSaving(false);
    }
  };

  const handleOpenPrint = () => {
    const params = new URLSearchParams();
    if (selectedPortfolioId) params.set('id', selectedPortfolioId);
    if (fromDate) params.set('from', fromDate);
    if (toDate) params.set('to', toDate);
    if (typeFilter !== 'all') params.set('type', typeFilter);
    if (outcomeFilter !== 'all' && outcomeFilter !== 'tagged' && outcomeFilter !== 'untagged') {
      // outcomeFilter holds an outcome item id, which is per-entry rather than
      // per-framework. The print view's `framework` param accepts a slug/id
      // so we resolve back to the framework that contains the selected outcome.
      const match = tagOptions.find((opt) => opt.id === outcomeFilter);
      if (match) params.set('framework', match.frameworkSlug);
    } else if (enabledFrameworks.length === 1) {
      params.set('framework', enabledFrameworks[0].slug);
    }
    const qs = params.toString();
    const url = qs ? `/portfolio/print?${qs}` : '/portfolio/print';
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener');
    }
  };

  // Compact row used inside the Accordion.Header. Two-line stack so the
  // title is unambiguous: top line is the module badge + full title, bottom
  // line is the labelled metadata + status chips. The chevron is added by
  // react-bootstrap at the right edge so we leave room for it with me-2.
  const renderRowSummary = (e) => {
    const moduleKind = e.entryKind || e.moduleType;
    const dateLabel = e.kind === 'cpd' ? 'Logged' : 'Completed';
    return (
      <div className="d-flex flex-column w-100 gap-1 me-2 portfolio-row-summary">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <Badge bg={MODULE_BADGE[moduleKind] || 'secondary'} text={moduleKind === 'cpd' ? 'dark' : undefined}>
            {MODULE_LABELS[moduleKind] || moduleKind}
          </Badge>
          <span className="fw-semibold" style={{ fontSize: '1.05rem' }}>
            {e.sourceTitle || '(untitled)'}
          </span>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2 small text-muted">
          <span><strong>{dateLabel}:</strong> {formatDateOnly(e.occurredAt)}</span>
          {e.kind === 'cpd' && e.entryType ? (
            <>
              <span className="text-muted">·</span>
              <span><strong>Type:</strong> {CPD_TYPE_LABELS[e.entryType] || e.entryType}</span>
            </>
          ) : null}
          {e.kind === 'cpd' && e.hours != null ? (
            <>
              <span className="text-muted">·</span>
              <span><strong>Hours:</strong> {Number(e.hours).toFixed(Number.isInteger(Number(e.hours)) ? 0 : 1)}h</span>
            </>
          ) : null}
          {e.kind === 'session' && e.status === 'completed' && e.effectiveScore != null ? (
            <>
              <span className="text-muted">·</span>
              <span>
                <strong>Score:</strong> {Math.round(e.effectiveScore)}%
                {e.scoreOverride != null ? (
                  <span className="ms-1" title="Lecturer adjusted">(adjusted)</span>
                ) : null}
              </span>
            </>
          ) : null}
          {/* Row chips are capped at 2 to keep the collapsed row scannable.
              Priority: lecturer-review-pending (an exception flag) is shown
              if set, then ONE of feedback / reflected. Tag count moves into
              the expanded body alongside the tag chips themselves. */}
          <div className="ms-auto d-flex flex-wrap gap-1">
            {e.lecturerReviewRequired ? (
              <Badge bg="warning" pill className="border" title="Lecturer review pending">
                <i className="bi bi-hourglass-split me-1" />Review pending
              </Badge>
            ) : null}
            {Array.isArray(e.lecturerComments) && e.lecturerComments.length > 0 ? (
              <Badge bg="info" pill className="border" title="Lecturer feedback">
                <i className="bi bi-chat-left-text me-1" />Feedback
              </Badge>
            ) : e.reflection?.body ? (
              <Badge bg="success" pill className="border" title="Reflection saved">
                <i className="bi bi-pencil-square me-1" />Reflected
              </Badge>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const renderEntryTags = (e) => {
    const tags = Array.isArray(e.tags) ? e.tags : [];
    if (tags.length === 0) return null;
    const enabledIds = new Set(enabledFrameworks.map((f) => f.id));
    return (
      <div className="d-flex flex-wrap gap-1 mb-2">
        {tags.map((t) => {
          const isLive = enabledIds.has(t.frameworkId);
          return (
            <Badge
              key={t.outcomeItemId}
              bg={isLive ? 'info' : 'light'}
              text={isLive ? undefined : 'muted'}
              className="border"
              title={isLive ? t.label : `${t.label} (framework currently disabled)`}
            >
              {t.code} — {t.label}
            </Badge>
          );
        })}
      </div>
    );
  };

  // Group tag options by framework -> domain for the picker modal.
  const groupedTagOptions = useMemo(() => {
    const byFw = new Map();
    for (const opt of tagOptions) {
      if (!byFw.has(opt.frameworkId)) {
        byFw.set(opt.frameworkId, {
          frameworkId: opt.frameworkId,
          frameworkName: opt.frameworkName,
          domains: new Map(),
        });
      }
      const fw = byFw.get(opt.frameworkId);
      const domainKey = `${opt.domainCode}|${opt.domainLabel}`;
      if (!fw.domains.has(domainKey)) {
        fw.domains.set(domainKey, {
          code: opt.domainCode,
          label: opt.domainLabel,
          items: [],
        });
      }
      fw.domains.get(domainKey).items.push(opt);
    }
    return [...byFw.values()].map((fw) => ({
      ...fw,
      domains: [...fw.domains.values()],
    }));
  }, [tagOptions]);

  // Build a quick lookup from `${entryKind}:${entryId}` → entry for
  // resolving goal links into title/module/date chips.
  const entryByKey = useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      map.set(`${e.entryKind}:${e.entryId}`, e);
    }
    return map;
  }, [entries]);

  const goalStatusBadge = (status) => {
    if (status === 'achieved') return <Badge bg="success"><i className="bi bi-check2 me-1" />Achieved</Badge>;
    if (status === 'not_started') return <Badge bg="secondary">Not started</Badge>;
    return <Badge bg="primary">In progress</Badge>;
  };

  const renderGoalsTab = () => {
    if (goalsLoading && goals.length === 0) {
      return <div className="text-center my-5"><Spinner animation="border" /></div>;
    }
    if (goalsError) {
      return <Alert variant="danger">{goalsError}</Alert>;
    }
    if (goals.length === 0) {
      return (
        <Card className="shadow-sm">
          <Card.Body className="text-center py-5">
            <i className="bi bi-flag" style={{ fontSize: '2rem', color: 'var(--app-primary)' }} />
            <h5 className="mt-3">Set your first learning goal</h5>
            <p className="text-muted mb-3">
              Goals are short, personal targets you want to work on — they tie
              your portfolio entries together as evidence of growth.
            </p>
            <Button variant="primary" onClick={openGoalCreate}>
              <i className="bi bi-plus-lg me-1" /> New goal
            </Button>
          </Card.Body>
        </Card>
      );
    }
    return (
      <div className="d-flex flex-column gap-3">
        {goals.map((goal) => {
          const links = Array.isArray(goal.links) ? goal.links : [];
          const linkedEntries = links
            .map((l) => entryByKey.get(`${l.entryKind}:${l.entryId}`))
            .filter(Boolean);
          const isAchieved = goal.status === 'achieved';
          return (
            <Card key={goal.id} className="shadow-sm">
              <Card.Body>
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                  <div className="flex-grow-1">
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                      {goalStatusBadge(goal.status)}
                      <h5 className="mb-0" style={isAchieved ? { textDecoration: 'line-through', color: 'var(--text-muted, #6c757d)' } : undefined}>
                        {goal.title}
                      </h5>
                    </div>
                    <div className="small text-muted d-flex flex-wrap gap-3">
                      {goal.targetDate ? (
                        <span><i className="bi bi-calendar-event me-1" />Target {formatDate(goal.targetDate)}</span>
                      ) : null}
                      {goal.achievedAt ? (
                        <span><i className="bi bi-check2-circle me-1" />Achieved {formatDate(goal.achievedAt)}</span>
                      ) : null}
                      <span>{linkedEntries.length} evidence entr{linkedEntries.length === 1 ? 'y' : 'ies'}</span>
                    </div>
                    {goal.description ? (
                      <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>{goal.description}</div>
                    ) : null}
                  </div>
                  <div className="d-flex flex-column gap-2 align-items-end portfolio-goal-actions">
                    <Button
                      size="sm"
                      variant={isAchieved ? 'outline-secondary' : 'outline-success'}
                      onClick={() => handleGoalStatusToggle(goal)}
                    >
                      <i className={`bi ${isAchieved ? 'bi-arrow-counterclockwise' : 'bi-check2'} me-1`} />
                      {isAchieved ? 'Reopen' : 'Mark achieved'}
                    </Button>
                    <div className="d-flex gap-2">
                      <Button size="sm" variant="outline-secondary" onClick={() => openGoalEdit(goal)}>
                        <i className="bi bi-pencil" />
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => setConfirmDeleteGoalId(goal.id)}>
                        <i className="bi bi-trash" />
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="mt-3 border-top pt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong className="small text-muted">Evidence from your portfolio</strong>
                    <Button size="sm" variant="outline-primary" onClick={() => openGoalLinkPicker(goal)}>
                      <i className="bi bi-link-45deg me-1" /> Link entries
                    </Button>
                  </div>
                  {linkedEntries.length === 0 ? (
                    <div className="small text-muted fst-italic">No entries linked yet.</div>
                  ) : (
                    <div className="d-flex flex-column gap-1">
                      {linkedEntries.map((e) => (
                        <div key={`${e.entryKind}:${e.entryId}`} className="d-flex flex-wrap align-items-center gap-2 small">
                          <Badge bg={MODULE_BADGE[e.entryKind] || 'secondary'} text={e.entryKind === 'cpd' ? 'dark' : undefined}>
                            {MODULE_LABELS[e.entryKind] || e.entryKind}
                          </Badge>
                          <span className="fw-semibold">{e.sourceTitle || '(untitled)'}</span>
                          <span className="text-muted">· {formatDate(e.occurredAt)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <div className="student-page">
      <Container className="mt-4 mb-5 student-page__content">
        <div className="student-dashboard-header shadow-sm rounded mb-3">
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <div>
              <h2 className="mb-2">My portfolios</h2>
              <p className="student-dashboard-header__copy mb-0">
                Group completed work into named portfolios for ARCP, job applications, or CPD evidence. Log CPD entries manually, or curate case studies and exercises from your activity feed.
              </p>
              {onBack ? (
                <Button type="button" variant="outline-primary" className="btn-sm mt-2" onClick={onBack}>
                  <i className="bi bi-arrow-left" aria-hidden="true" />{' '}
                  Back
                </Button>
              ) : null}
            </div>
          </div>
        </div>

        <ButtonGroup className="mb-3 flex-wrap">
          <Button
            variant={activeTab === 'entries' ? 'primary' : 'outline-primary'}
            onClick={() => setActiveTab('entries')}
          >
            <i className="bi bi-journal-text me-1" /> Entries
          </Button>
          <Button
            variant={activeTab === 'goals' ? 'primary' : 'outline-primary'}
            onClick={() => setActiveTab('goals')}
          >
            <i className="bi bi-flag me-1" /> Goals
            {goals.length > 0 ? (
              <Badge bg="light" text="dark" className="ms-2">{goals.length}</Badge>
            ) : null}
          </Button>
        </ButtonGroup>

        {activeTab === 'goals' ? renderGoalsTab() : null}
        {activeTab === 'entries' ? (
          <>

        <Card className="mb-3 shadow-sm portfolio-list-card">
          <Card.Body>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <div className="d-flex align-items-center gap-2">
                <h5 className="mb-0">Your portfolios</h5>
                <Badge bg="secondary" pill>{portfolios.length}</Badge>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => { setCreateForm({ name: '', description: '' }); setCreateError(''); setShowCreateModal(true); }}
                >
                  <i className="bi bi-plus-lg me-1" /> New portfolio
                </Button>
                {portfolios.length > 0 ? (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => { setEditingPortfolioId(null); setShowManageModal(true); }}
                  >
                    <i className="bi bi-pencil-square me-1" /> Manage
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="portfolio-tile-row">
              <button
                type="button"
                className={`portfolio-tile ${selectedPortfolioId === '' ? 'portfolio-tile--active' : ''}`}
                onClick={() => setSelectedPortfolioId('')}
              >
                <div className="portfolio-tile__title">All activity</div>
                <div className="portfolio-tile__count">{entries.length} {entries.length === 1 ? 'entry' : 'entries'}</div>
                <div className="portfolio-tile__sub">Everything you've completed or logged</div>
              </button>
              {portfolios.map((p) => (
                <button
                  type="button"
                  key={p.id}
                  className={`portfolio-tile ${selectedPortfolioId === p.id ? 'portfolio-tile--active' : ''}`}
                  onClick={() => setSelectedPortfolioId(p.id)}
                >
                  <div className="portfolio-tile__title">{p.name}</div>
                  <div className="portfolio-tile__count">{p.entryCount} {p.entryCount === 1 ? 'entry' : 'entries'}</div>
                  {p.description ? (
                    <div className="portfolio-tile__sub">{p.description}</div>
                  ) : null}
                </button>
              ))}
            </div>
          </Card.Body>
        </Card>

        <Card className="mb-3 shadow-sm">
          <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <div className="fw-semibold">
                {currentPortfolio
                  ? <>Viewing <span className="text-primary">{currentPortfolio.name}</span></>
                  : 'Viewing all activity'}
              </div>
              <div className="small text-muted">
                {currentPortfolio
                  ? 'Open any entry below to add a reflection, tag outcomes, or remove it from this portfolio.'
                  : 'Open any entry below and use "Add to portfolio" to curate it into one of your named portfolios.'}
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button variant="primary" onClick={openCpdCreate}>
                <i className="bi bi-plus-lg me-1" />
                Log new CPD entry
              </Button>
              {currentPortfolio ? (
                <Button variant="outline-primary" onClick={handleOpenShareModal}>
                  <i className="bi bi-share me-1" /> Share externally
                </Button>
              ) : null}
              <Button variant="outline-primary" onClick={handleOpenPrint}>
                <i className="bi bi-printer me-1" />
                Print/save portfolio
              </Button>
              <Button variant="outline-secondary" onClick={() => setShowFrameworkModal(true)}>
                <i className="bi bi-tags me-1" />
                {enabledFrameworks.length > 0
                  ? `Framework mapping (${enabledFrameworks.length})`
                  : 'Framework mapping'}
              </Button>
            </div>
          </Card.Body>
        </Card>

        {currentPortfolio ? (
          <Card className="mb-3 shadow-sm">
            <Card.Body>
              <Row className="g-3">
                <Col xs={6} md={enabledFrameworks.length > 0 ? 2 : 3}>
                  <div className="small text-muted">Total entries</div>
                  <div className="h4 mb-0">{summary.total}</div>
                </Col>
                <Col xs={6} md={enabledFrameworks.length > 0 ? 2 : 3}>
                  <div className="small text-muted">CPD hours logged</div>
                  <div className="h4 mb-0">{summary.totalCpdHours.toFixed(1)}h</div>
                </Col>
                <Col xs={6} md={enabledFrameworks.length > 0 ? 2 : 3}>
                  <div className="small text-muted">With reflection</div>
                  <div className="h4 mb-0">{summary.reflectedCount}</div>
                </Col>
                {enabledFrameworks.length > 0 ? (
                  <Col xs={6} md={2}>
                    <div className="small text-muted">Tagged</div>
                    <div className="h4 mb-0">{summary.taggedCount}</div>
                  </Col>
                ) : null}
                <Col xs={12} md={enabledFrameworks.length > 0 ? 4 : 3}>
                  <div className="small text-muted">By module</div>
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {Object.entries(summary.byType).length === 0 ? (
                      <span className="text-muted small">No entries yet</span>
                    ) : (
                      Object.entries(summary.byType)
                        .sort((a, b) => b[1] - a[1])
                        .map(([type, count]) => (
                          <Badge key={type} bg="light" text="dark" className="border">
                            {MODULE_LABELS[type] || type}: {count}
                          </Badge>
                        ))
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        ) : null}

        <Card className="mb-3 portfolio-filters">
          <Card.Body className="py-3">
            <Row className="g-3 align-items-end">
              <Col xs={12} md={6} lg={enabledFrameworks.length > 0 ? 3 : 4}>
                <Form.Label className="small text-muted mb-1">Type</Form.Label>
                <Form.Select
                  size="sm"
                  value={typeFilter}
                  onChange={(ev) => setTypeFilter(ev.target.value)}
                >
                  {TYPE_FILTERS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} md={6} lg={enabledFrameworks.length > 0 ? 3 : 4}>
                <Form.Label className="small text-muted mb-1">Reflection</Form.Label>
                <Form.Select
                  size="sm"
                  value={reflectionFilter}
                  onChange={(ev) => setReflectionFilter(ev.target.value)}
                >
                  {REFLECTION_FILTERS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
              </Col>
              {enabledFrameworks.length > 0 ? (
                <Col xs={12} md={12} lg={3}>
                  <Form.Label className="small text-muted mb-1">Outcome</Form.Label>
                  <Form.Select
                    size="sm"
                    value={outcomeFilter}
                    onChange={(ev) => setOutcomeFilter(ev.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="tagged">Any tag</option>
                    <option value="untagged">Untagged only</option>
                    {groupedTagOptions.map((fw) => (
                      <optgroup key={fw.frameworkId} label={fw.frameworkName}>
                        {fw.domains.map((d) => (
                          <React.Fragment key={`${fw.frameworkId}-${d.code}`}>
                            {d.items.map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.code} — {o.label}
                              </option>
                            ))}
                          </React.Fragment>
                        ))}
                      </optgroup>
                    ))}
                  </Form.Select>
                </Col>
              ) : null}
              <Col xs={12} md={12} lg={enabledFrameworks.length > 0 ? 3 : 4}>
                <Form.Label className="small text-muted mb-1">Date range</Form.Label>
                <div className="d-flex gap-1 align-items-center">
                  <Form.Control
                    type="date"
                    size="sm"
                    value={fromDate}
                    onChange={(ev) => setFromDate(ev.target.value)}
                  />
                  <span className="small text-muted">→</span>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={toDate}
                    onChange={(ev) => setToDate(ev.target.value)}
                  />
                </div>
              </Col>
            </Row>
            <div className="d-flex justify-content-end gap-2 mt-2">
              <Button size="sm" variant="link" className="px-1" onClick={resetFilters}>Reset filters</Button>
              <Button size="sm" variant="outline-secondary" onClick={refresh} disabled={loading}>
                <i className="bi bi-arrow-clockwise me-1" /> Refresh
              </Button>
            </div>
          </Card.Body>
        </Card>

        {error ? <Alert variant="danger">{error}</Alert> : null}

        {loading ? (
          <div className="text-center mt-4"><Spinner animation="border" /></div>
        ) : visible.length === 0 ? (
          <Alert variant="info">
            {entries.length === 0
              ? "Your portfolio is empty. Complete a case, exercise, or log a CPD entry to get started."
              : 'No entries match the current filters.'}
          </Alert>
        ) : (
          <Accordion alwaysOpen={false} className="portfolio-entry-list">
            {visible.map((e) => {
              const key = entryKey(e);
              const draftValue = reflectionValueFor(e);
              const baseValue = e.reflection?.body || '';
              const dirty = draftValue !== baseValue;
              const feedback = reflectionFeedback[key] || '';
              const tagCount = Array.isArray(e.tags) ? e.tags.length : 0;
              return (
                <Accordion.Item eventKey={key} key={key}>
                  <Accordion.Header>{renderRowSummary(e)}</Accordion.Header>
                  <Accordion.Body>
                    {renderEntryTags(e)}
                    {e.kind === 'cpd' && e.cpdReflection ? (
                      <div className="small text-muted mb-3" style={{ whiteSpace: 'pre-wrap' }}>
                        <strong>CPD notes:</strong> {e.cpdReflection}
                      </div>
                    ) : null}
                    {e.kind === 'session' && e.reviewComment ? (
                      <div className="small text-muted mb-3" style={{ whiteSpace: 'pre-wrap' }}>
                        <strong>Auto-grading review:</strong> {e.reviewComment}
                      </div>
                    ) : null}
                    {Array.isArray(e.lecturerComments) && e.lecturerComments.length > 0 ? (
                      <div className="mb-3">
                        <div className="small text-muted mb-2">
                          <strong>Lecturer feedback</strong>
                        </div>
                        {e.lecturerComments.map((c) => (
                          <div
                            key={c.id}
                            className="p-3 rounded mb-2"
                            style={{ background: '#eef6ff', whiteSpace: 'pre-wrap' }}
                          >
                            <div className="small text-muted mb-1">
                              <strong>{c.lecturerName || 'Lecturer'}</strong>
                              <span className="ms-2">{formatDate(c.updatedAt)}</span>
                            </div>
                            {c.body}
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <Form.Group>
                      <Form.Label className="small text-muted mb-1">
                        Your reflection
                        {e.reflection?.updatedAt ? (
                          <span className="ms-2">last saved {formatDate(e.reflection.updatedAt)}</span>
                        ) : null}
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="What did you learn? What would you do differently next time?"
                        value={draftValue}
                        onChange={(ev) => handleReflectionChange(e, ev.target.value)}
                      />
                      <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                        <span className="small text-muted">{feedback}</span>
                        <div className="d-flex gap-2 flex-wrap portfolio-entry-actions">
                          {selectedPortfolioId ? (
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => removeFromCurrentPortfolio(e)}
                            >
                              <i className="bi bi-folder-minus me-1" /> Remove from portfolio
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => openMembershipModal(e)}
                            >
                              <i className="bi bi-folder-plus me-1" />
                              {(e.portfolioIds || []).length > 0
                                ? `In ${e.portfolioIds.length} portfolio${e.portfolioIds.length === 1 ? '' : 's'}`
                                : 'Add to portfolio'}
                            </Button>
                          )}
                          {enabledFrameworks.length > 0 ? (
                            <Button
                              size="sm"
                              variant="outline-info"
                              onClick={() => openTagModal(e)}
                            >
                              <i className="bi bi-tags me-1" />
                              {tagCount > 0 ? `Tags (${tagCount})` : 'Tag outcomes'}
                            </Button>
                          ) : null}
                          {e.kind === 'cpd' ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => openCpdEdit(e)}
                              >
                                <i className="bi bi-pencil me-1" /> Edit CPD
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => setConfirmDeleteCpdId(e.entryId)}
                              >
                                <i className="bi bi-trash me-1" /> Delete
                              </Button>
                            </>
                          ) : null}
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={!dirty || savingReflectionKey === key}
                            onClick={() => handleSaveReflection(e)}
                          >
                            {savingReflectionKey === key ? 'Saving…' : 'Save reflection'}
                          </Button>
                        </div>
                      </div>
                    </Form.Group>
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        )}
          </>
        ) : null}
      </Container>

      <Modal show={showCpdModal} onHide={() => setShowCpdModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{cpdForm.id ? 'Edit CPD entry' : 'Add CPD entry'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cpdError ? <Alert variant="danger" className="mb-3">{cpdError}</Alert> : null}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={cpdForm.title}
                onChange={(ev) => setCpdForm((p) => ({ ...p, title: ev.target.value }))}
                placeholder="e.g. Attended palliative care study day"
                autoFocus
              />
            </Form.Group>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Type</Form.Label>
                  <Form.Select
                    value={cpdForm.entryType}
                    onChange={(ev) => setCpdForm((p) => ({ ...p, entryType: ev.target.value }))}
                  >
                    {CPD_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Hours</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.25"
                    min="0"
                    max="24"
                    value={cpdForm.hours}
                    onChange={(ev) => setCpdForm((p) => ({ ...p, hours: ev.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={cpdForm.occurredOn}
                    onChange={(ev) => setCpdForm((p) => ({ ...p, occurredOn: ev.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mt-3">
              <Form.Label>Notes (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={cpdForm.reflection}
                onChange={(ev) => setCpdForm((p) => ({ ...p, reflection: ev.target.value }))}
                placeholder="Key learning, references, follow-up actions…"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowCpdModal(false)} disabled={cpdSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCpdSave} disabled={cpdSaving}>
            {cpdSaving ? 'Saving…' : (cpdForm.id ? 'Save changes' : 'Add entry')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(confirmDeleteCpdId)} onHide={() => setConfirmDeleteCpdId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete CPD entry?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This permanently removes the CPD entry and any reflection attached to it.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmDeleteCpdId(null)} disabled={deletingCpdId != null}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteCpd} disabled={deletingCpdId != null}>
            {deletingCpdId != null ? 'Deleting…' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showFrameworkModal} onHide={() => setShowFrameworkModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Outcome frameworks</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Enable a framework to tag your portfolio entries against its learning outcomes.
            Tagging is optional — leave this off if your programme doesn&apos;t use a mapped framework.
          </p>
          {frameworks.length === 0 ? (
            <Alert variant="info" className="mb-0">No frameworks are available.</Alert>
          ) : (
            <div className="d-flex flex-column gap-2">
              {frameworks.map((fw) => (
                <Card key={fw.id} className="shadow-sm">
                  <Card.Body className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                    <div>
                      <div className="fw-semibold">
                        {fw.name}
                        {fw.isGlobal ? (
                          <Badge bg="light" text="dark" className="ms-2 border">Global</Badge>
                        ) : null}
                      </div>
                      {fw.description ? (
                        <div className="small text-muted">{fw.description}</div>
                      ) : null}
                      <div className="small text-muted mt-1">{fw.itemCount} outcome items</div>
                    </div>
                    <Form.Check
                      type="switch"
                      checked={fw.enabled}
                      disabled={Boolean(frameworkBusy[fw.id])}
                      onChange={() => handleToggleFramework(fw)}
                      label={fw.enabled ? 'Enabled' : 'Disabled'}
                    />
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowFrameworkModal(false)}>Done</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(tagModalEntry)} onHide={() => setTagModalEntry(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Tag outcomes
            {tagModalEntry?.sourceTitle ? (
              <span className="text-muted ms-2 small">— {tagModalEntry.sourceTitle}</span>
            ) : null}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {tagError ? <Alert variant="danger">{tagError}</Alert> : null}
          {groupedTagOptions.length === 0 ? (
            <Alert variant="info" className="mb-0">
              No outcomes available. Enable a framework first via the &quot;Outcome frameworks&quot; button.
            </Alert>
          ) : (
            <div className="d-flex flex-column gap-3">
              {groupedTagOptions.map((fw) => (
                <div key={fw.frameworkId}>
                  <div className="fw-semibold mb-2">{fw.frameworkName}</div>
                  {fw.domains.map((domain) => (
                    <div key={`${fw.frameworkId}-${domain.code}`} className="mb-2 ps-2">
                      <div className="small text-muted mb-1">
                        {domain.code ? `${domain.code} — ` : ''}{domain.label}
                      </div>
                      <div className="d-flex flex-column gap-1">
                        {domain.items.map((o) => (
                          <Form.Check
                            key={o.id}
                            id={`tag-${o.id}`}
                            type="checkbox"
                            checked={tagSelection.has(o.id)}
                            onChange={() => toggleTagSelection(o.id)}
                            label={
                              <span>
                                <span className="text-muted me-1">{o.code}</span>
                                {o.label}
                              </span>
                            }
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <span className="small text-muted me-auto">
            {tagSelection.size} selected
          </span>
          <Button variant="outline-secondary" onClick={() => setTagModalEntry(null)} disabled={tagSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveTags} disabled={tagSaving || groupedTagOptions.length === 0}>
            {tagSaving ? 'Saving…' : 'Save tags'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create a portfolio</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createError ? <Alert variant="danger" className="mb-3">{createError}</Alert> : null}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={createForm.name}
                onChange={(ev) => setCreateForm((p) => ({ ...p, name: ev.target.value }))}
                placeholder="e.g. GPhC ARCP 2026"
                autoFocus
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={createForm.description}
                onChange={(ev) => setCreateForm((p) => ({ ...p, description: ev.target.value }))}
                placeholder="What is this portfolio for?"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowCreateModal(false)} disabled={createSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreatePortfolio} disabled={createSaving}>
            {createSaving ? 'Creating…' : 'Create portfolio'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showManageModal} onHide={() => { setShowManageModal(false); setEditingPortfolioId(null); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Manage portfolios</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {portfolios.length === 0 ? (
            <Alert variant="info" className="mb-0">You haven&apos;t created any portfolios yet.</Alert>
          ) : (
            <div className="d-flex flex-column gap-2">
              {portfolios.map((p) => {
                const isEditing = editingPortfolioId === p.id;
                return (
                  <Card key={p.id} className="shadow-sm">
                    <Card.Body>
                      {isEditing ? (
                        <Form>
                          <Form.Group className="mb-2">
                            <Form.Label className="small text-muted mb-1">Name</Form.Label>
                            <Form.Control
                              value={editingForm.name}
                              onChange={(ev) => setEditingForm((prev) => ({ ...prev, name: ev.target.value }))}
                            />
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label className="small text-muted mb-1">Description</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={editingForm.description}
                              onChange={(ev) => setEditingForm((prev) => ({ ...prev, description: ev.target.value }))}
                            />
                          </Form.Group>
                          <div className="d-flex gap-2 justify-content-end">
                            <Button size="sm" variant="outline-secondary" onClick={() => setEditingPortfolioId(null)} disabled={manageBusy}>
                              Cancel
                            </Button>
                            <Button size="sm" variant="primary" onClick={handleSavePortfolioEdit} disabled={manageBusy}>
                              {manageBusy ? 'Saving…' : 'Save changes'}
                            </Button>
                          </div>
                        </Form>
                      ) : (
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                          <div>
                            <div className="fw-semibold">{p.name}</div>
                            {p.description ? <div className="small text-muted">{p.description}</div> : null}
                            <div className="small text-muted mt-1">{p.entryCount} entr{p.entryCount === 1 ? 'y' : 'ies'}</div>
                          </div>
                          <div className="d-flex gap-2">
                            <Button size="sm" variant="outline-secondary" onClick={() => openEditPortfolio(p)}>
                              <i className="bi bi-pencil me-1" /> Edit
                            </Button>
                            <Button size="sm" variant="outline-danger" onClick={() => setConfirmDeletePortfolioId(p.id)}>
                              <i className="bi bi-trash me-1" /> Delete
                            </Button>
                          </div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => { setShowManageModal(false); setEditingPortfolioId(null); }}>
            Done
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(confirmDeletePortfolioId)} onHide={() => setConfirmDeletePortfolioId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete portfolio?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This removes the portfolio and its membership records. The underlying activity entries
          (case studies, CPD entries, reflections) are kept.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmDeletePortfolioId(null)} disabled={manageBusy}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDeletePortfolio} disabled={manageBusy}>
            {manageBusy ? 'Deleting…' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(membershipModalEntry)} onHide={() => setMembershipModalEntry(null)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Add to portfolio
            {membershipModalEntry?.sourceTitle ? (
              <div className="small text-muted">— {membershipModalEntry.sourceTitle}</div>
            ) : null}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {membershipError ? <Alert variant="danger">{membershipError}</Alert> : null}
          {portfolios.length === 0 ? (
            <div>
              <p className="text-muted small">
                You don&apos;t have any portfolios yet. Create one first.
              </p>
              <Button
                variant="primary"
                onClick={() => {
                  setMembershipModalEntry(null);
                  setCreateForm({ name: '', description: '' });
                  setShowCreateModal(true);
                }}
              >
                <i className="bi bi-plus-lg me-1" /> Create a portfolio
              </Button>
            </div>
          ) : (
            <Form>
              {portfolios.map((p) => (
                <Form.Check
                  key={p.id}
                  id={`membership-${p.id}`}
                  type="checkbox"
                  checked={membershipSelection.has(p.id)}
                  onChange={() => toggleMembership(p.id)}
                  label={
                    <span>
                      <strong>{p.name}</strong>
                      <span className="text-muted small ms-2">{p.entryCount} entr{p.entryCount === 1 ? 'y' : 'ies'}</span>
                    </span>
                  }
                />
              ))}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setMembershipModalEntry(null)} disabled={membershipSaving}>
            Cancel
          </Button>
          {portfolios.length > 0 ? (
            <Button variant="primary" onClick={handleSaveMembership} disabled={membershipSaving}>
              {membershipSaving ? 'Saving…' : 'Save'}
            </Button>
          ) : null}
        </Modal.Footer>
      </Modal>

      <Modal show={showShareModal} onHide={() => setShowShareModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Share externally
            {currentPortfolio ? (
              <div className="small text-muted">— {currentPortfolio.name}</div>
            ) : null}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Anyone with a share link can view this portfolio without logging in. Set an expiry,
            or revoke a link at any time when it&apos;s no longer needed.
          </p>
          {shareError ? <Alert variant="danger">{shareError}</Alert> : null}

          <Card className="mb-3 shadow-sm">
            <Card.Body>
              <div className="small fw-semibold mb-2">Create a new share link</div>
              <Row className="g-2 align-items-end">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small text-muted mb-1">
                      Label (optional, helps you keep track)
                    </Form.Label>
                    <Form.Control
                      size="sm"
                      value={shareForm.label}
                      onChange={(ev) => setShareForm((p) => ({ ...p, label: ev.target.value }))}
                      placeholder="e.g. ARCP panel 2026"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="small text-muted mb-1">
                      Expires after
                    </Form.Label>
                    <Form.Select
                      size="sm"
                      value={shareForm.expiresInDays}
                      onChange={(ev) => setShareForm((p) => ({ ...p, expiresInDays: ev.target.value }))}
                    >
                      <option value="">Never</option>
                      <option value="7">7 days</option>
                      <option value="30">30 days</option>
                      <option value="90">90 days</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Button
                    variant="primary"
                    size="sm"
                    className="w-100"
                    onClick={handleCreateShareLink}
                    disabled={shareCreating}
                  >
                    {shareCreating ? '…' : 'Create'}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <div className="small fw-semibold mb-2">Existing links</div>
          {shareLoading ? (
            <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
          ) : shareLinks.length === 0 ? (
            <Alert variant="info" className="mb-0 small">
              You haven&apos;t shared this portfolio yet.
            </Alert>
          ) : (
            <div className="d-flex flex-column gap-2">
              {shareLinks.map((link) => {
                const url = buildShareUrl(link.token);
                const status = link.revokedAt
                  ? { label: 'Revoked', variant: 'secondary' }
                  : (link.expiresAt && new Date(link.expiresAt) <= new Date())
                    ? { label: 'Expired', variant: 'secondary' }
                    : { label: 'Active', variant: 'success' };
                return (
                  <Card key={link.id} className="shadow-sm">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                        <div>
                          <span className="fw-semibold">
                            {link.label || 'Untitled link'}
                          </span>
                          <Badge bg={status.variant} className="ms-2">{status.label}</Badge>
                        </div>
                        <div className="small text-muted">
                          <i className="bi bi-eye me-1" />{link.viewCount} view{link.viewCount === 1 ? '' : 's'}
                          {link.lastViewedAt ? (
                            <span className="ms-2">last {formatDate(link.lastViewedAt)}</span>
                          ) : null}
                        </div>
                      </div>
                      <div className="d-flex flex-wrap gap-2 small text-muted mb-2">
                        <span><strong>Created:</strong> {formatDate(link.createdAt)}</span>
                        {link.expiresAt ? (
                          <span><strong>Expires:</strong> {formatDate(link.expiresAt)}</span>
                        ) : null}
                      </div>
                      <Form.Control
                        size="sm"
                        readOnly
                        value={url}
                        onFocus={(ev) => ev.target.select()}
                        style={{ fontFamily: 'monospace', fontSize: 12 }}
                      />
                      <div className="d-flex gap-2 mt-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleCopyShareLink(link)}
                        >
                          <i className="bi bi-clipboard me-1" />
                          {copiedLinkId === link.id ? 'Copied!' : 'Copy URL'}
                        </Button>
                        <Button
                          size="sm"
                          variant={link.revokedAt ? 'outline-success' : 'outline-warning'}
                          onClick={() => handleToggleShareRevoke(link)}
                          disabled={Boolean(shareLinkBusy[link.id])}
                        >
                          {link.revokedAt ? (
                            <><i className="bi bi-arrow-counterclockwise me-1" />Restore</>
                          ) : (
                            <><i className="bi bi-slash-circle me-1" />Revoke</>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDeleteShareLink(link)}
                          disabled={Boolean(shareLinkBusy[link.id])}
                        >
                          <i className="bi bi-trash me-1" />Delete
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowShareModal(false)}>Done</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showGoalModal} onHide={() => setShowGoalModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{goalForm.id ? 'Edit goal' : 'New goal'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {goalError ? <Alert variant="danger">{goalError}</Alert> : null}
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              autoFocus
              value={goalForm.title}
              onChange={(ev) => setGoalForm((f) => ({ ...f, title: ev.target.value }))}
              placeholder="e.g. Build confidence with insulin regimens"
              maxLength={200}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={goalForm.description}
              onChange={(ev) => setGoalForm((f) => ({ ...f, description: ev.target.value }))}
              placeholder="What does success look like? What evidence will count?"
            />
          </Form.Group>
          <Row className="g-3">
            <Col sm={6}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={goalForm.status}
                  onChange={(ev) => setGoalForm((f) => ({ ...f, status: ev.target.value }))}
                >
                  <option value="not_started">Not started</option>
                  <option value="in_progress">In progress</option>
                  <option value="achieved">Achieved</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group>
                <Form.Label>Target date (optional)</Form.Label>
                <Form.Control
                  type="date"
                  value={goalForm.targetDate || ''}
                  onChange={(ev) => setGoalForm((f) => ({ ...f, targetDate: ev.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowGoalModal(false)} disabled={goalSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleGoalSave} disabled={goalSaving}>
            {goalSaving ? 'Saving…' : (goalForm.id ? 'Save changes' : 'Create goal')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!confirmDeleteGoalId} onHide={() => setConfirmDeleteGoalId(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete goal?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This will remove the goal and any links to portfolio entries. The entries
          themselves stay where they are.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmDeleteGoalId(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleGoalDelete}>Delete</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!goalLinkPicker} onHide={() => setGoalLinkPicker(null)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Link evidence{goalLinkPicker ? ` — ${goalLinkPicker.goalTitle}` : ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {goalLinkError ? <Alert variant="danger">{goalLinkError}</Alert> : null}
          <p className="small text-muted">
            Tick the portfolio entries that contributed to this goal. Untick to remove.
          </p>
          {entries.length === 0 ? (
            <Alert variant="info">You don&apos;t have any portfolio entries yet — complete a case or log a CPD entry first.</Alert>
          ) : (
            <div className="d-flex flex-column gap-1">
              {entries.map((e) => {
                const key = `${e.entryKind}:${e.entryId}`;
                const checked = goalLinkPicker?.selection.has(key) || false;
                return (
                  <Form.Check
                    key={key}
                    type="checkbox"
                    id={`goal-link-${key}`}
                    checked={checked}
                    onChange={(ev) => {
                      const isChecked = ev.target.checked;
                      setGoalLinkPicker((prev) => {
                        if (!prev) return prev;
                        const next = new Set(prev.selection);
                        if (isChecked) next.add(key); else next.delete(key);
                        return { ...prev, selection: next };
                      });
                    }}
                    label={
                      <span className="d-inline-flex flex-wrap align-items-center gap-2">
                        <Badge bg={MODULE_BADGE[e.entryKind] || 'secondary'} text={e.entryKind === 'cpd' ? 'dark' : undefined}>
                          {MODULE_LABELS[e.entryKind] || e.entryKind}
                        </Badge>
                        <span className="fw-semibold">{e.sourceTitle || '(untitled)'}</span>
                        <span className="small text-muted">· {formatDate(e.occurredAt)}</span>
                      </span>
                    }
                  />
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setGoalLinkPicker(null)} disabled={goalLinkSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleGoalLinkSave} disabled={goalLinkSaving}>
            {goalLinkSaving ? 'Saving…' : 'Save links'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default StudentPortfolioView;
